import {
  Filter,
  type SelectItem,
  useSelectQueryGate,
} from '@vrplatform/components';
import { useApiQuery, useInfiniteApiQuery, useTeam } from '@vrplatform/data';
import { CrossCircleIcon } from '@vrplatform/icons';
import { Box, Center, Group, Text, Tooltip } from '@mantine/core';
import type { Maybe } from '@vrplatform/utils';
import { useMemo, useState } from 'react';
import type { BaseCustomFilterProps } from './_types';
import {
  getRecurringFeeFilterQuery,
  getRecurringFeeGroup,
  getRecurringFeeTitle,
  type RecurringFeeType,
} from './RecurringFeeFilter.utils';

interface Props extends BaseCustomFilterProps {
  recurringFeeId: Maybe<string>;
  setRecurringFeeId: (value: string | null) => void;
  includeInactive?: boolean;
  allowUnassigned?: boolean;
  type?: RecurringFeeType;
}

type RecurringFeeOptionSource = {
  id: string;
  name?: string | null;
  status?: string | null;
  type?: RecurringFeeType | null;
};

const mapFee = (
  fee: RecurringFeeOptionSource,
  showGroup = true
): SelectItem => {
  const title = getRecurringFeeTitle(fee.name);

  return {
    label:
      fee.status === 'inactive'
        ? ((
            <Group w="100%" wrap="nowrap" justify="space-between">
              <Text>{title}</Text>
              <Tooltip label="This fee is archived">
                <Box
                  sx={(theme) => ({
                    width: 8,
                    height: 8,
                    backgroundColor: theme.colors.yellow[6],
                    borderRadius: '50%',
                  })}
                />
              </Tooltip>
            </Group>
          ) as any)
        : title,
    value: fee.id,
    group: showGroup ? getRecurringFeeGroup(fee.type) : undefined,
  };
};

export const RecurringFeeFilter = ({
  recurringFeeId,
  setRecurringFeeId,
  label = 'Fee & Commission',
  includeInactive = false,
  allowUnassigned = true,
  type,
}: Props) => {
  const [{ id: teamId }] = useTeam();
  const gate = useSelectQueryGate({ value: recurringFeeId });
  const [search, setSearch] = useState('');

  const queryData = useInfiniteApiQuery(
    async (api, signal, variables, pageParam) => {
      const response = await api.GET('/recurring-fees', {
        params: {
          query: getRecurringFeeFilterQuery({
            page: pageParam,
            search: variables.search,
            includeInactive: variables.includeInactive,
            type: variables.type,
          }),
        },
        signal,
      });

      if (response.error) {
        throw response.error;
      }

      return {
        list: response.data.data.map((fee) => mapFee(fee, !variables.type)),
        aggregate: response.data.pagination.total,
        pagination: response.data.pagination,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchOptions,
      queryGroup: 'fees',
      uniqueQueryKey: 'recurring-fee-filter',
      queryKeys: ['customFees'],
      variables: {
        teamId,
        search: search.trim(),
        type,
        includeInactive,
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => lastPage.pagination.nextPage ?? null,
    }
  );

  const { data } = useApiQuery(
    async (api, signal, { feeId }) => {
      if (!feeId) {
        return { value: null };
      }

      if (feeId === 'inactive') {
        return {
          value: {
            label: 'Unassigned',
            value: 'inactive',
          },
        };
      }

      const response = await api.GET('/recurring-fees/{id}', {
        params: {
          path: {
            id: feeId,
          },
        },
        signal,
      });

      if (response.error) {
        throw response.error;
      }

      return {
        value: mapFee(response.data, false),
      };
    },
    {
      skip: !teamId || !gate.shouldFetchValue,
      queryGroup: 'fees',
      uniqueQueryKey: 'recurring-fee-filter-value',
      queryKeys: ['customFees'],
      keepPreviousData: true,
      variables: {
        feeId: recurringFeeId,
        teamId,
      },
    }
  );

  const customBottomActions = useMemo(
    () =>
      allowUnassigned
        ? [
            {
              id: 'add-fee',
              label: 'Unassigned',
              icon: (
                <Center>
                  <CrossCircleIcon size={14} />
                </Center>
              ),
              onSubmit: () => {
                setRecurringFeeId('inactive');
              },
            },
          ]
        : undefined,
    [setRecurringFeeId, allowUnassigned]
  );

  return (
    <Filter.Select
      type="single"
      value={gate.hasValue ? data?.value || null : null}
      setValue={(next) => setRecurringFeeId(next?.value || null)}
      label={label}
      withinPortal
      onOpenChange={gate.onOpenChange}
      infiniteData={{ ...queryData, setSearch }}
      hideIcon
      customBottomActions={customBottomActions}
    />
  );
};
